import html2pdf from "html2pdf.js";
import { useEffect, useRef, useState } from "react";
import { DateTimeFormat } from './../components/DateFormat/DateTimeFormat';

const ExpenseVoucherPDF = ({ data, onComplete, expenseDetails = [], empType }) => {
  const contentRef = useRef(null);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (!data) return;

    setIsReady(true);

    const generatePDF = async () => {
      try {
        await new Promise(resolve => requestAnimationFrame(resolve));
        await new Promise(resolve => setTimeout(resolve, 300));

        const element = contentRef.current;
        if (!element) return;

        const options = {
          margin: [10, 10, 10, 10],
          filename: `Expense_Voucher_${empType || "Unknown"}.pdf`,
          html2canvas: {
            scale: 2,
            useCORS: true,
            scrollY: 0,
            ignoreElements: (element) => element.id === 'ignore-element',
            onclone: (clonedDoc) => {
              const clonedElement = clonedDoc.getElementById('pdf-content');
              if (clonedElement) {
                clonedElement.style.visibility = 'visible';
                clonedElement.style.position = 'relative';
                clonedElement.style.width = '190mm';
              }
            }
          },
          jsPDF: {
            unit: "mm",
            format: "a4",
            orientation: "portrait",
            hotfixes: ["px_scaling"]
          }
        };

        await html2pdf().set(options).from(element).save();
        if (onComplete) onComplete();
      } catch (error) {
        console.error('PDF generation failed:', error);
        if (onComplete) onComplete();
      }
    };

    generatePDF();
  }, [data, empType, onComplete]);

  if (!isReady) return null;

  const getCategoryWiseTotals = () => {
    const categoryTotals = {};

    expenseDetails.forEach(item => {
      const categoryName = item.expenseCategory?.name || "Uncategorized";
      const amount = parseFloat(item.amount) || 0;

      if (categoryTotals[categoryName]) {
        categoryTotals[categoryName] += amount;
      } else {
        categoryTotals[categoryName] = amount;
      }
    });

    return categoryTotals;
  };

  const formatDMY = (date) => {
    if (!(date instanceof Date) || isNaN(date)) return 'N/A';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const getDateRange = () => {
    if (!expenseDetails || expenseDetails.length === 0) return { from: 'N/A', to: 'N/A' };

    const dates = expenseDetails.map(item => new Date(item.date)).filter(date => !isNaN(date));
    if (dates.length === 0) return { from: 'N/A', to: 'N/A' };

    const minDate = new Date(Math.min(...dates));
    const maxDate = new Date(Math.max(...dates));

    return {
      from: formatDMY(minDate),
      to: formatDMY(maxDate)
    };
  };

  const dateRange = getDateRange();
  const totalExpenseAmount = expenseDetails.reduce((total, item) => total + (parseFloat(item.amount) || 0), 0).toFixed(2);

  return (
    <div
      id="pdf-content"
      ref={contentRef}
      style={{
        background: "#fff",
        padding: "20px",
        width: "190mm",
        minHeight: "277mm",
        fontFamily: "Arial, sans-serif",
        color: "#000",
        boxSizing: "border-box",
        fontSize: "14px",
        margin: "0 auto",
      }}
    >
      {/* Header Section */}
      <div style={{ textAlign: "center", marginBottom: "20px" }}>
        <h1 style={{ margin: "0", fontSize: "20px", textTransform: "uppercase", letterSpacing: "1px" }}>
          Expense Voucher
        </h1>
        <div style={{ display: "flex", justifyContent: "center", margin: "10px 0" }}>
          <div style={{ margin: "0 15px" }}>
            <strong>From:</strong> {dateRange.from}
          </div>
          <div style={{ margin: "0 15px" }}>
            <strong>To:</strong> {dateRange.to}
          </div>
        </div>
        <div style={{
          display: "flex",
          justifyContent: "center",
          margin: "10px 0",
          lineHeight: "1.6",
          paddingBottom: "15px",
        }}>
          <strong>Voucher No:</strong> {data?.id}
        </div>
        <hr style={{ margin: "10px auto", borderTop: "2px solid #000", width: "60%" }} />
      </div>

      {/* Summary Section */}
      <div style={{ marginBottom: "20px" }}>
        <h3 style={{ fontSize: "16px", borderBottom: "2px solid #ccc", paddingBottom: "5px", marginBottom: "10px" }}>
          Employee Details
        </h3>
        <table style={{ width: "100%", borderCollapse: "collapse", lineHeight: "1.6" }}>
          <tbody>
            <tr>
              <td><strong>Employee ID:</strong></td>
              <td>{data.empid || 'N/A'}</td>
              <td><strong>Full Name:</strong></td>
              <td>{data.fullName || 'N/A'}</td>
            </tr>
            <tr>
              <td><strong>Project:</strong></td>
              <td>{data.project_name || 'N/A'}</td>
              <td><strong>Status:</strong></td>
              <td>{data.status || 'N/A'}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Main Content */}
      <div>
        {/* Expense Breakdown Table */}
        {Array.isArray(expenseDetails) && expenseDetails.length > 0 && (
          <>
            <h3 style={{ fontSize: "16px", borderBottom: "2px solid #ccc", paddingBottom: "5px", marginBottom: "10px" }}>
              Expense Breakdown
            </h3>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                marginBottom: "10px",
                border: "1px solid #ccc"
              }}
            >
              <thead style={{ backgroundColor: "#f2f2f2" }}>
                <tr>
                  <th style={headerStyle}>Date</th>
                  <th style={headerStyle}>Description</th>
                  <th style={headerStyle}>Category</th>
                  <th style={headerStyle}>Amount (₹)</th>
                </tr>
              </thead>
              <tbody>
                {expenseDetails.map((item, idx) => (
                  <tr key={idx} style={{ pageBreakInside: 'avoid' }}>
                    <td style={cellStyle}>{DateTimeFormat(item.date) || 'N/A'}</td>
                    <td style={cellStyle}>{item.description || 'N/A'}</td>
                    <td style={cellStyle}>{item.expenseCategory?.name || 'N/A'}</td>
                    <td style={cellStyle}>₹{item.amount || '0.00'}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Expense Breakdown Total - Table के नीचे */}
            <div style={{
              display: "flex",
              justifyContent: "flex-end",
              marginBottom: "20px"
            }}>
              <table style={{
                width: "300px",
                borderCollapse: "collapse",
                border: "1px solid #ccc"
              }}>
                <tbody>
                  <tr style={{ backgroundColor: "#f9f9f9", fontWeight: "bold" }}>
                    <td style={{
                      ...cellStyle,
                      textAlign: "right",
                      paddingRight: "20px",
                      fontWeight: "bold",
                      borderRight: "none"
                    }}>
                      Total Expenses:
                    </td>
                    <td style={{
                      ...cellStyle,
                      fontWeight: "bold",
                      borderLeft: "none",
                      textAlign: "right"
                    }}>
                      ₹{totalExpenseAmount}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Category-wise Totals */}
            <div style={{ marginBottom: "20px", pageBreakInside: "avoid" }}>
              <h3 style={{ fontSize: "16px", borderBottom: "2px solid #ccc", paddingBottom: "5px", marginBottom: "10px" }}>
                Category-wise Totals
              </h3>
              <table style={{ width: "100%", borderCollapse: "collapse", border: "1px solid #ccc" }}>
                <thead style={{ backgroundColor: "#f2f2f2" }}>
                  <tr>
                    <th style={headerStyle}>Category</th>
                    <th style={headerStyle}>Total Amount (₹)</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(getCategoryWiseTotals()).map(([category, total], idx) => (
                    <tr key={idx}>
                      <td style={cellStyle}>{category}</td>
                      <td style={cellStyle}>₹{total.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Total Amount Section - यहाँ से signature sections शुरू होंगे */}
            <div style={{
              marginTop: "30px",
              pageBreakBefore: "auto"
            }}>
              <h3 style={{ fontSize: "16px", borderBottom: "2px solid #ccc", paddingBottom: "5px", marginBottom: "10px" }}>
                Total Amount
              </h3>
              <div style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "10px 15px",
                backgroundColor: "#f9f9f9",
                border: "1px solid #ccc",
                borderRadius: "4px",
                marginBottom: "50px"
              }}>
                <strong>Total Amount to be Reimbursed:</strong>
                <strong style={{ fontSize: "18px" }}>₹{data.total_amount || "0.00"}</strong>
              </div>

              {/* Signature Sections - Total Reimbursed के नीचे */}
              <div style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                marginTop: "60px",
                pageBreakInside: "avoid"
              }}>
                {/* Prepared By - Left Side */}
                <div style={{
                  textAlign: "center",
                  width: "45%",
                  marginTop: "20px"
                }}>
                  <div style={{
                    padding: "10px",
                    minHeight: "100px"
                  }}>
                    <p style={{
                      margin: "0 0 10px 0",
                      fontWeight: "bold",
                      fontSize: "14px"
                    }}>
                      Prepared By:
                    </p>
                    <div style={{
                      borderTop: "1px solid #000",
                      width: "200px",
                      height: "1px",
                      margin: "60px auto 5px auto",
                    }}></div>
                    <p style={{
                      margin: "5px 0 0 0",
                      fontSize: "12px"
                    }}>
                      Signature
                    </p>
                  </div>
                </div>

                {/* Approved By - Right Side */}
                <div style={{
                  textAlign: "center",
                  width: "45%",
                  marginTop: "20px"
                }}>
                  <div style={{
                    padding: "10px",
                    minHeight: "100px"
                  }}>
                    <p style={{
                      margin: "0 0 10px 0",
                      fontWeight: "bold",
                      fontSize: "14px"
                    }}>
                      Approved By:
                    </p>
                    <div style={{
                      borderTop: "1px solid #000",
                      width: "200px",
                      height: "1px",
                      margin: "60px auto 5px auto",
                    }}></div>
                    <p style={{
                      margin: "5px 0 0 0",
                      fontSize: "12px"
                    }}>
                      Signature
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const headerStyle = {
  padding: "8px",
  border: "1px solid #ccc",
  fontWeight: "bold",
  textAlign: "left",
  fontSize: "13px"
};

const cellStyle = {
  padding: "6px",
  border: "1px solid #ccc",
  textAlign: "left",
  fontSize: "12px"
};

export default ExpenseVoucherPDF;